import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type JiraConnectionStatus = 'ACTIVE' | 'NEEDS_REAUTH' | 'PENDING_SITE_SELECTION';

export interface JiraSite {
  id: string;    // cloudId
  url: string;
  name: string;
  scopes: string[];
}

export interface JiraDefaults {
  configured:  boolean;
  projectKey:  string | null;
  updatedAt?:  string;
}

export interface JiraConnection {
  status:             JiraConnectionStatus;
  cloudId?:           string;
  siteUrl?:           string;
  siteName?:          string;
  atlassianAccountId?: string;
  scopes?:            string;
  connectedAt?:       string;
  updatedAt?:         string;
  // Present only when status === 'PENDING_SITE_SELECTION'
  pendingSites?:      JiraSite[];
}

@Injectable({ providedIn: 'root' })
export class JiraService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/jira`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Starts the Jira OAuth 2.0 (3LO) flow.
   * Returns the Atlassian authorization URL; the caller should redirect the
   * browser to it (window.location.href = authUrl).
   */
  startConnect(): Observable<{ authUrl: string }> {
    return this.http.get<{ authUrl: string }>(`${this.base}/connect`);
  }

  /**
   * Returns the current user's Jira connection (active, pending, or 404 → null).
   * Use status === 'PENDING_SITE_SELECTION' to detect the multi-site case.
   */
  getConnection(): Observable<JiraConnection | null> {
    return this.http.get<JiraConnection>(`${this.base}/connection`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /**
   * Finalizes a pending multi-site connection by selecting a specific cloudId.
   * Only valid when getConnection() returns status === 'PENDING_SITE_SELECTION'.
   */
  selectSite(cloudId: string): Observable<JiraConnection> {
    return this.http.post<JiraConnection>(`${this.base}/select-site`, { cloudId });
  }

  /**
   * Removes the Jira connection for the current user.
   * Does NOT revoke the Atlassian OAuth token.
   */
  disconnect(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/connection`);
  }

  /** Returns the currently configured default Jira project key for notifications. */
  getDefaults(): Observable<JiraDefaults> {
    return this.http.get<JiraDefaults>(`${this.base}/defaults`);
  }

  /** Saves the default Jira project key for notifications. */
  saveDefaults(projectKey: string): Observable<JiraDefaults> {
    return this.http.put<JiraDefaults>(`${this.base}/defaults`, { projectKey });
  }
}
